import { addDays, format, getDate, subDays } from 'date-fns';
import PerfectScrollbar from 'react-perfect-scrollbar';
import pt from 'date-fns/locale/pt';
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiMail } from 'react-icons/fi';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';

import api from 'services/api';
import {
  SubstituicaoItem,
  ComercialItem,
  ClienteItem,
} from 'pages/Eleicoes/@types';
import {
  Column,
  Container as ContainerTable,
} from '../Painel/PlanoMidia/styles';
import { Container } from '../styles';
import AppError from 'utils/AppError';

import getValidationErrors from 'services/getValidationErros';
import { useModal } from 'hooks/modal';
import { Role } from 'utils/variables';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import { IRootState } from 'store/modules/rootReducer';
import { show } from 'store/modules/dialog/actions';

import SelectDate from 'components/SelectDate';
import DropDownMenu from 'components/DropDownMenu';
import TextArea from 'components/TextArea';
import AsyncSelect from 'components/AsyncSelect/withoutForm';
import Button from 'components/Button';
import Avatar from 'components/Avatar';

interface ComercialOption extends ComercialItem {
  value: number;
  label: string;
  isDisabled: boolean;
}
export default function Substituicao() {
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { profile, emissora, currentDate } = useSelector(
    (state: IRootState) => state.usuario,
  ) as any;
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /**
   * USE STATE
   */
  const [substituicoes, setSubstituicoes] = useState<SubstituicaoItem[]>([]);
  const [selected, setSelected] = useState({} as SubstituicaoItem);
  const [loadingMail, setLoadingMail] = useState(false);
  const [, setLoadingTable] = useState(false);

  /**
   * Hooks
   */
  const { showModal, hideModal, updateModal, modalData } = useModal();
  const formRef = useRef<FormHandles>(null);
  /**
   * USE CALLBACK
   */
  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );
  const handleSelectMaterial = useCallback(
    (comercial: ComercialItem, substituicao: SubstituicaoItem) => {
      setSelected(substituicao);
      const optionBase = {
        type: 'question',
        text: `Associar o comercial ${comercial.titulo} ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/eleicoes/substituicao/${substituicao.id}`,
          method: api.put,
          action: 'vinculacao',
          data: { comercial_id: comercial.id },
        },
        haveCallback: true,
        successMessage: 'Comercial associado!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleCheck = useCallback(
    (substituicao: SubstituicaoItem) => {
      if (
        substituicao.insercao &&
        substituicao.comercialde &&
        (substituicao.comercialpara || substituicao.observacao)
      ) {
        setSelected(substituicao);
        const optionBase = {
          type: 'question',
          text: `Confirmar substituição do material ${substituicao.comercialde.titulo}?`,
          confirmButtonText: 'CONFIRMAR',
          callback: {
            url: `/eleicoes/substituicao/${substituicao.id}`,
            method: api.put,
            action: 'check',
          },
          haveCallback: true,
          successMessage: 'Substituição confirmada!',
          cancelButtonText: 'NÃO',
          showCancelButton: false,
          showConfirmButton: true,
          showCloseButton: true,
        };
        dispatch(show(optionBase));
      } else {
        toast.error(
          'Não é permitido sinalizar uma substituição não finalizada',
        );
      }
    },
    [dispatch],
  );

  const updateSubstituicoes = useCallback((substituicao: SubstituicaoItem) => {
    setSubstituicoes(prevState =>
      prevState.map(s => {
        return s.id === substituicao.id ? substituicao : s;
      }),
    );
  }, []);

  const renderObservacao = useCallback(
    (substituicao: SubstituicaoItem) => {
      const handleSubmit = async (data: any) => {
        if (!formRef.current) {
          return;
        }
        try {
          formRef.current.setErrors({});

          const schema = Yup.object().shape({
            observacao: Yup.string().required('Preencha a observação'),
          });

          await schema.validate({ ...data }, { abortEarly: false });

          const response = await api.put(
            `/eleicoes/substituicao/${substituicao.id}`,
            {
              ...data,
            },
          );
          if (response.data) updateSubstituicoes(response.data);
          toast.success(`Observação incluída`);

          hideModal();
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);
            formRef.current.setErrors(errors);
            return;
          }
          if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      };

      return (
        <Form ref={formRef} onSubmit={handleSubmit}>
          <TextArea
            placeholder="Observação"
            name="observacao"
            height={150}
            disable={false}
            errorField=""
            icon=""
          />

          <div className="actions">
            <Button color="default" onClick={() => hideModal()}>
              CANCELAR
            </Button>
            <Button type="submit" color="primary">
              CONFIRMAR
            </Button>
          </div>
        </Form>
      );
    },
    [hideModal, updateSubstituicoes],
  );

  const handleObservacao = useCallback(
    (substituicao: SubstituicaoItem) => {
      try {
        showModal({
          title: `Observação`,
          component: renderObservacao(substituicao),
        });
      } catch (error) {
        console.log(error);
      }
    },
    [renderObservacao, showModal],
  );

  const removeSubstituicao = useCallback((substituicao: SubstituicaoItem) => {
    setSubstituicoes(prevState =>
      prevState.filter(s => s.id !== substituicao.id),
    );
  }, []);

  const handleDelete = useCallback(
    (substituicao: SubstituicaoItem) => {
      showModal({
        title: 'ATENÇÃO',
        component: <p>Remover substituição?</p>,
        callback: async () => {
          updateModal({ ...modalData, loading: true });
          try {
            await api.delete(`/eleicoes/substituicao/${substituicao.id}`);
            toast.success('Substituição removida');
            removeSubstituicao(substituicao);
            hideModal();
          } catch (error) {
            if (error instanceof Error) toast.error(error.message);
            else {
              const appError = error as AppError;
              toast.error(appError.message);
            }
          } finally {
            // updateModal({ ...modalData, loading: true });
          }
        },
      });
    },
    [hideModal, modalData, removeSubstituicao, showModal, updateModal],
  );

  const handleActionSelect = useCallback(
    async (action: number, substituicao: SubstituicaoItem) => {
      switch (action) {
        case 1:
          handleObservacao(substituicao);
          break;
        case 2:
          handleDelete(substituicao);
          break;
      }
    },
    [handleDelete, handleObservacao],
  );
  const loadComerciais = useCallback(
    async (value: string, cliente: ClienteItem, cargo: string) => {
      // if (value.length > 0) {
      try {
        if (cliente) {
          const response = await api.get<ComercialItem[]>(
            `/comerciaists/eleicoes`,
            {
              params: {
                cliente_id: cliente.id,
                cargo,
                ...(value && { search: value }),
                limit: 10,
              },
            },
          );
          return response.data.map(comercial => ({
            value: comercial.id,
            label: `${comercial.codmaterial} - ${comercial.titulo}`,
            isDisabled: comercial.veto,
            ...comercial,
          }));
        } else {
          toast.error('Cliente não identificado');
        }
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
      // }
    },
    [],
  );

  const sendMail = useCallback(async () => {
    setLoadingMail(true);
    try {
      if (substituicoes && substituicoes.length > 0) {
        await api.post(`/eleicoes/substituicao`, null, {
          params: {
            emissora_id: emissora.value,
            data: format(new Date(currentDate), 'yyyy-MM-dd'),
          },
        });
      } else {
        toast.error('Nenhuma substituição encontrada');
      }
    } catch (error) {
      if (error instanceof Error) toast.error(error.message);
      else {
        const appError = error as AppError;
        toast.error(appError.message);
      }
    }
    setLoadingMail(false);
  }, [currentDate, emissora.value, substituicoes]);
  /**
   * USE MEMO
   */
  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    }
  }, [currentDate]);

  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextvDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  /**
   * USE EFFECT
   */
  useEffect(() => {
    setLoadingTable(true);
    api
      .get('/eleicoes/substituicao', {
        params: {
          emissora_id: emissora.value,
          data: format(new Date(currentDate), 'yyyy-MM-dd'),
        },
      })
      .then(response => {
        setSubstituicoes(response.data);
      })
      .catch(error => console.log(error))
      .finally(() => setLoadingTable(false));
  }, [currentDate, emissora.value]);

  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (
        callbackDialog.success.action === 'vinculacao' ||
        callbackDialog.success.action === 'check'
      ) {
        setSubstituicoes(prevState =>
          prevState.map(s => {
            return s.id === selected.id ? callbackDialog.success.data : s;
          }),
        );
      }
    }
  }, [callbackDialog, selected.id]);

  return (
    <Container>
      <header>
        <section>
          <h6>SUBSTITUIÇÃO</h6>
        </section>

        {dateFormatted && new Date(currentDate) && (
          <SelectDate
            dateFormatted={dateFormatted}
            currentDateZoned={String(new Date(currentDate))}
            handleNextDay={handleNextvDay}
            handleDateChange={handleDateChange}
            handlePrevDay={handlePrevDay}
            disabledNextDay={
              getDate(new Date(currentDate)) === getDate(new Date())
            }
          />
        )}
        <Button
          className="sendMailButton"
          color="primary"
          onClick={() => sendMail()}
          disabled={!substituicoes || substituicoes.length === 0}
        >
          {loadingMail ? (
            'Enviando e-mail...'
          ) : (
            <>
              Enviar E-mail
              <FiMail className="mailicon" />
            </>
          )}
        </Button>
        {/* <section style={{ minWidth: '150px' }} /> */}
      </header>

      <ContainerTable>
        <PerfectScrollbar className="scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: '100px' }}>DATA</th>
                <th style={{ width: '100px' }}>BL/SQ</th>
                <th style={{ width: '250px' }}>PROGRAMA</th>
                <th style={{ width: '100px' }}>IP/PT</th>
                <th>DE</th>
                <th>PARA</th>
                <th style={{ width: '45px' }}>CHECK</th>
                <th style={{ width: '45px' }}>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {!!substituicoes &&
                substituicoes.map((s, index) => (
                  <tr key={index}>
                    <td>
                      <strong>
                        {s.insercao.linharoteiro
                          ? format(
                              new Date(s.insercao.linharoteiro.data),
                              'dd/MM/yyyy',
                            )
                          : ''}
                      </strong>
                    </td>
                    <td>
                      <Column>
                        <strong>BLOCO {s.insercao.bloco}</strong>
                        <span>SEQ {s.insercao.sequencia}</span>
                      </Column>
                    </td>
                    <td>
                      <strong>
                        {s.insercao.linharoteiro
                          ? s.insercao.linharoteiro.programa.nome
                          : ''}
                      </strong>
                    </td>
                    <td>
                      <strong>{`${
                        s.insercao.linharoteiro
                          ? s.insercao.linharoteiro.ippt
                          : ''
                      } ${
                        s.insercao.linharoteiro
                          ? s.insercao.linharoteiro.numippt
                          : ''
                      }`}</strong>
                    </td>

                    <td className="comercialSubstituicao">
                      {`${s.comercialde.codmaterial} - ${s.comercialde.titulo}`}
                      <strong>({s.comercialde.cliente.mnemonico})</strong>
                    </td>
                    {s.observacao ? (
                      <td>
                        <p>{s.observacao}</p>
                      </td>
                    ) : s.comercialpara ? (
                      <td className="comercialSubstituicao">
                        {`${s.comercialpara.codmaterial} - ${s.comercialpara.titulo}`}
                        <strong>({s.comercialpara.cliente.mnemonico})</strong>
                      </td>
                    ) : (
                      <td>
                        <AsyncSelect
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          searchable
                          name="editor"
                          cacheOptions
                          type="default"
                          defaultOptions
                          loadOptions={(value: string) =>
                            loadComerciais(
                              value,
                              s.comercialde.cliente,
                              s.insercao.cargo,
                            )
                          }
                          placeholder="Selecione o material"
                          isClearable
                          onChange={(selectedMaterial: ComercialOption) =>
                            handleSelectMaterial(selectedMaterial, s)
                          }
                          isMulti={false}
                          disabled={
                            profile.role !== Role.ADMIN &&
                            profile.role !== Role.OPEC
                          }
                        />
                      </td>
                    )}
                    <td>
                      {s.checkedby ? (
                        <Avatar
                          size="40"
                          name={s.checkedby.nome}
                          src={s.checkedby.avatar_url}
                          round
                        />
                      ) : (
                        <Button
                          fill
                          color="font"
                          round
                          disabled={
                            !s.comercialpara ||
                            !s.observacao ||
                            (profile.role !== Role.ADMIN &&
                              profile.role !== Role.PROG)
                          }
                          onClick={() => handleCheck(s)}
                        />
                      )}
                    </td>
                    <td>
                      <DropDownMenu
                        id={`acoes${index}`}
                        disabled={false}
                        actions={[
                          {
                            value: 1,
                            label: 'ADD OBS',
                            disabled:
                              profile.role !== Role.ADMIN &&
                              profile.role !== Role.OPEC,
                          },
                          {
                            value: 2,
                            label: 'REMOVER',
                            disabled:
                              profile.role !== Role.ADMIN &&
                              profile.role !== Role.OPEC,
                          },
                        ]}
                        handleClick={(action: {
                          value: number;
                          label: string;
                        }) => handleActionSelect(action.value, s)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </PerfectScrollbar>
      </ContainerTable>
    </Container>
  );
}
